import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { products } from '../data/products';
import { ShoppingCart, Check, Truck, ShieldCheck, ArrowLeft } from 'lucide-react';
interface ProductDetailPageProps {
  productId: number;
  onNavigate: (page: 'home' | 'archive' | 'detail', productId?: number) => void;
}
export function ProductDetailPage({
  productId,
  onNavigate
}: ProductDetailPageProps) {
  const product = products.find(p => p.id === productId) || products[0];
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-281">
      <Header data-id="element-282" />

      <main className="flex-grow" data-id="element-283">
        {/* Breadcrumb Bar */}
        <div className="bg-gray-100 border-b border-gray-200 py-3" data-id="element-284">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-285">
            <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-286">
              Home
            </button>
            <span data-id="element-287">/</span>
            <button onClick={() => onNavigate('archive')} className="hover:text-primary transition-colors" data-id="element-288">
              Shop
            </button>
            <span data-id="element-289">/</span>
            <span className="text-charcoal" data-id="element-290">{product.name}</span>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12" data-id="element-291">
          <button onClick={() => onNavigate('archive')} className="flex items-center gap-2 text-gray-500 font-bold uppercase text-sm mb-8 hover:text-primary transition-colors group" data-id="element-292">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-293" />{' '}
            Back to Products
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-294">
            {/* Left Column: Images */}
            <div className="space-y-6" data-id="element-295">
              <div className="bg-white border border-gray-200 p-8 md:p-16 relative overflow-hidden group" data-id="element-296">
                <img src={product.image} alt={product.name} className="w-full h-auto object-contain mix-blend-multiply hover:scale-105 transition-transform duration-500" data-id="element-297" />
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-298" />
              </div>
              <div className="grid grid-cols-4 gap-4" data-id="element-299">
                {[1, 2, 3, 4].map(i => <div key={i} className={`bg-white border p-2 cursor-pointer hover:border-primary transition-colors ${i === 1 ? 'border-primary' : 'border-gray-200'}`} data-id="element-300">
                    <img src={product.image} alt="Thumbnail" className="w-full h-full object-contain mix-blend-multiply opacity-80 hover:opacity-100" data-id="element-301" />
                  </div>)}
              </div>
            </div>

            {/* Right Column: Details */}
            <div data-id="element-302">
              <div className="mb-2 flex items-center gap-3" data-id="element-303">
                <span className="text-primary font-bold uppercase tracking-wider text-sm" data-id="element-304">
                  {product.category}
                </span>
                <span className="w-1 h-1 bg-gray-300 rounded-full" data-id="element-305" />
                <span className="text-gray-500 font-medium text-sm" data-id="element-306">
                  SKU: {product.sku}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-heading font-bold text-charcoal uppercase italic leading-none mb-4" data-id="element-307">
                {product.name}
              </h1>

              <div className="flex items-end gap-4 mb-6 pb-6 border-b border-gray-200" data-id="element-308">
                <span className="text-4xl font-heading font-bold text-primary italic" data-id="element-309">
                  {product.price}
                </span>
                {product.inStock ? <span className="flex items-center gap-1 text-green-600 font-bold text-sm mb-2" data-id="element-310">
                    <Check className="w-4 h-4" data-id="element-311" /> In Stock & Ready to Ship
                  </span> : <span className="text-red-500 font-bold text-sm mb-2" data-id="element-312">
                    Out of Stock
                  </span>}
              </div>

              <p className="text-gray-600 text-lg leading-relaxed mb-8" data-id="element-313">
                {product.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-314">
                <button className="flex-1 bg-primary text-white py-4 px-8 font-heading font-bold text-xl uppercase italic tracking-wider hover:bg-charcoal transition-colors skew-x-[-10deg] flex items-center justify-center gap-2" data-id="element-315">
                  <span className="skew-x-[10deg] flex items-center gap-2" data-id="element-316">
                    <ShoppingCart className="w-5 h-5" data-id="element-317" /> Add to Cart
                  </span>
                </button>
                <button className="flex-1 border-2 border-charcoal text-charcoal py-4 px-8 font-heading font-bold text-xl uppercase italic tracking-wider hover:bg-charcoal hover:text-white transition-colors skew-x-[-10deg]" data-id="element-318">
                  <span className="block skew-x-[10deg]" data-id="element-319">Save for Later</span>
                </button>
              </div>

              <div className="bg-gray-50 p-6 border border-gray-200 mb-8" data-id="element-320">
                <h3 className="font-heading font-bold text-xl uppercase italic text-charcoal mb-4" data-id="element-321">
                  Key Features
                </h3>
                <ul className="space-y-3" data-id="element-322">
                  {product.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-323">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" data-id="element-324" />
                      <span className="text-gray-700 font-medium" data-id="element-325">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-326">
                <div className="flex items-center gap-4 p-4 border border-gray-100 bg-white shadow-sm" data-id="element-327">
                  <Truck className="w-8 h-8 text-primary" data-id="element-328" />
                  <div data-id="element-329">
                    <h4 className="font-bold text-charcoal uppercase text-sm" data-id="element-330">
                      Free Shipping
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-331">On orders over $999</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 border border-gray-100 bg-white shadow-sm" data-id="element-332">
                  <ShieldCheck className="w-8 h-8 text-primary" data-id="element-333" />
                  <div data-id="element-334">
                    <h4 className="font-bold text-charcoal uppercase text-sm" data-id="element-335">
                      Lifetime Warranty
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-336">
                      Structural guarantee
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Specs Section */}
          <div className="mt-20" data-id="element-337">
            <h2 className="text-3xl font-heading font-bold text-charcoal uppercase italic mb-8 border-b border-gray-200 pb-4" data-id="element-338">
              Technical Specifications
            </h2>
            <div className="bg-white border border-gray-200 overflow-hidden" data-id="element-339">
              <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-340">
                {product.specs.map((spec, idx) => <div key={idx} className={`flex items-center p-4 border-b border-gray-100 ${idx % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'} ${idx >= product.specs.length - 2 ? 'border-b-0' : ''}`} data-id="element-341">
                    <span className="w-1/3 font-bold text-gray-500 uppercase text-sm" data-id="element-342">
                      {spec.label}
                    </span>
                    <span className="w-2/3 font-medium text-charcoal" data-id="element-343">
                      {spec.value}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-344" />
    </div>;
}